/**
 * Configuração e GATES das gravações de sessão (rrweb).
 *
 * Funções puras, sem Angular e sem o gravador: testáveis isoladamente e sem
 * custo no bundle eager — o `@rrweb/record` só entra pelo chunk lazy
 * (`gravacoes-runtime.ts`), depois de todo gate aqui dizer sim.
 *
 * Espelha `openreplay/openreplay-config.ts` de propósito (mesmas duas
 * decisões, mesmos motivos, mesma amostragem por device): o rrweb SUCEDE o
 * OpenReplay, e a regra "quem grava" não muda com o gravador. Ver GRAVACOES.md.
 */

import { inSample } from '../trace-utils';
import { ehLocalhost } from '../gate-utils';

/** Bloco `gravacoes` dos environments. */
export interface GravacoesConfig {
  /**
   * Endpoint de ingestão (`POST /i` do stack `aws-sam-gravacoes`). **Vazio =
   * desligado** — é o kill switch: sem endpoint o chunk nem é baixado.
   */
  ingestPoint: string;
  /**
   * % de DISPOSITIVOS que gravam (0-100), determinística por device (a mesma
   * pessoa está sempre dentro ou sempre fora). Alavanca mestre de custo: aqui
   * o custo é REQUISIÇÃO (um lote a cada ~3 s por device gravando) e upload no
   * celular do consumidor, não armazenamento. Escada: 1 → 5 → 25 → 100.
   */
  clientSamplePct: number;
  /** Lojas em que a gravação vale. **Lista vazia = todas.** */
  restaurantAllowList: number[];
  /**
   * Furar o gate de localhost — SÓ no `environment.local.ts`. Ausente/false em
   * todo environment que vai a deploy.
   */
  permitirLocalhost?: boolean;
}

export interface GravacoesGateContext {
  production: boolean;
  hostname: string;
  isCypress: boolean;
  doNotTrack: boolean;
  deviceId: string;
  restaurantId: number | null;
  restaurantModules: { [k: string]: any } | null;
}

export type MotivoBloqueio =
  | 'sem-ingest-point'
  | 'nao-producao'
  | 'localhost'
  | 'cypress'
  | 'do-not-track'
  | 'fora-da-amostra'
  | 'loja-fora-do-escopo'
  | '';

export interface DecisaoCarga {
  carregar: boolean;
  motivo: MotivoBloqueio;
}

/** Esta loja está no escopo? Loja ainda não resolvida (`null`) NÃO reprova. */
export function lojaNoEscopo(cfg: GravacoesConfig, ctx: GravacoesGateContext): boolean {
  if (!cfg.restaurantAllowList || cfg.restaurantAllowList.length === 0) return true;
  if (ctx.restaurantId === null || ctx.restaurantId === undefined) return true;
  return cfg.restaurantAllowList.indexOf(ctx.restaurantId) !== -1;
}

/** Gates que não dependem da loja — os únicos avaliáveis no bootstrap. */
export function decidirAgendamento(
  cfg: GravacoesConfig | undefined,
  ctx: GravacoesGateContext,
): DecisaoCarga {
  if (!cfg || !cfg.ingestPoint) return { carregar: false, motivo: 'sem-ingest-point' };
  if (ctx.isCypress) return { carregar: false, motivo: 'cypress' };
  if (!ctx.production) return { carregar: false, motivo: 'nao-producao' };
  if (ehLocalhost(ctx.hostname) && !cfg.permitirLocalhost) return { carregar: false, motivo: 'localhost' };
  if (ctx.doNotTrack) return { carregar: false, motivo: 'do-not-track' };
  // Sem id estável não há coorte estável (ver openreplay-config para o porquê
  // de `inSample('')` ser perigoso).
  if (!ctx.deviceId) return { carregar: false, motivo: 'fora-da-amostra' };
  if (!inSample(ctx.deviceId, cfg.clientSamplePct)) return { carregar: false, motivo: 'fora-da-amostra' };
  return { carregar: true, motivo: '' };
}

/** Todos os gates, reavaliados no `load` (com a loja na mão). */
export function decidirCarga(
  cfg: GravacoesConfig | undefined,
  ctx: GravacoesGateContext,
): DecisaoCarga {
  const base = decidirAgendamento(cfg, ctx);
  if (!base.carregar) return base;
  if (!lojaNoEscopo(cfg as GravacoesConfig, ctx)) {
    return { carregar: false, motivo: 'loja-fora-do-escopo' };
  }
  return { carregar: true, motivo: '' };
}

// ───────────────────────── Etapas do funil ─────────────────────────
//
// Cada evento do funil de vendas entra na linha do tempo da gravação como
// custom event do rrweb, e o Lambda de ingestão extrai o nome dali para o
// índice — é o que faz o painel poder filtrar "quem revisou o pedido e não
// comprou".
//
// A lista existe em três lugares (aqui, `lote.py` na stack e
// `Recordings.constants.jsx` no painel) porque não há pacote compartilhado
// entre TypeScript, Python e o painel. Divergir não quebra nada em voz alta:
// vira filtro que não acha nada. Mexeu aqui, mexa nos três.

/**
 * A abertura do cardápio, marcada pelo PRÓPRIO gravador.
 *
 * O `screen_view` do OpenPanel nasce dentro do SDK e não passa pelo `track()`,
 * então nunca chegaria aqui pela fila. Mas o gravador sabe que a tela abriu —
 * ele está gravando —, e marcar isso resolve duas coisas de uma vez:
 *
 * 1. **"abriu o cardápio e não fez mais nada" vira um filtro.** É a maior perda
 *    do funil, e sem esta marca ela seria a única faixa impossível de assistir.
 * 2. **Separa "não fez nada" de "não sei".** Uma gravação anterior a isto não
 *    tem etapa nenhuma, e sem a marca de abertura ela seria indistinguível de
 *    quem só olhou o cardápio e saiu.
 *
 * Uma vez por sessão, no começo. Não é por tela como no OpenPanel: para o
 * filtro (que é por sessão) dá no mesmo, e um marcador por navegação encheria
 * a barra do player de ruído.
 */
export const ETAPA_ABERTURA = 'screen_view';

/** Etapas na ORDEM da jornada. A primeira é marcada pelo gravador (acima); as
 * outras chegam pela fila, espelhando o que já vai para o OpenPanel. */
export const ETAPAS_DO_FUNIL: ReadonlyArray<string> = [
  ETAPA_ABERTURA, 'add_to_cart', 'view_cart', 'begin_checkout', 'checkout_submitted', 'purchase',
];

/**
 * Etapas que fecham o lote na hora.
 *
 * São as duas em que a sessão costuma acabar logo depois — o cliente envia o
 * pedido e sai, às vezes para um gateway externo. O lote normal esperaria até
 * 3 s por uma descarga que pode não vir, e perder justamente o fim do funil
 * é perder o filtro que se quer usar. Custa no máximo dois POSTs por sessão.
 */
export const ETAPAS_QUE_FECHAM_LOTE: ReadonlyArray<string> = ['checkout_submitted', 'purchase'];

export function ehEtapaDoFunil(nome: string): boolean {
  return ETAPAS_DO_FUNIL.indexOf(nome) !== -1;
}

export function fechaLote(nome: string): boolean {
  return ETAPAS_QUE_FECHAM_LOTE.indexOf(nome) !== -1;
}

/** Tag do custom event. Uma por etapa (e não uma genérica) porque o player
 * pinta o marcador na barra de progresso pela tag: assim cada etapa ganha a
 * própria cor sem trabalho nenhum. */
export function tagDoFunil(nome: string): string {
  return 'funil:' + nome;
}

/**
 * O que vai DENTRO do evento gravado.
 *
 * Só o valor numérico e o instante real. As `props` do OpenPanel podem
 * carregar dado do consumidor, e este payload vai cru para o S3 — o que não
 * precisa estar lá não entra. E o `ts` viaja explícito porque o evento pode
 * ser injetado bem depois de ter acontecido: o gravador entra no idle, e o
 * histórico é drenado nesse momento.
 */
export function payloadDoFunil(props: any, ts: number): { v?: number; ts: number } {
  const valor = props && typeof props.value === 'number' && isFinite(props.value)
    ? props.value
    : undefined;
  return valor === undefined ? { ts } : { v: valor, ts };
}
